import {HStack, Icon, Link, Show, Space, Text} from 'platform/foundation';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {Nullish, TestIdProps} from 'shared/web';

import {Tooltip} from '../Tooltip/Tooltip';
import {getMaxLengthCounterText} from './getMaxLengthCounterText';
import type {LabelActions} from './LabelActions';

export interface LabelProps extends TestIdProps {
  children: string | Nullish;
  id?: string;
  maxLength?: number;
  currentLength?: number;
  isCounterVisible?: boolean;
  isRequired?: boolean | Nullish;
  isRecommended?: boolean | Nullish;
  tooltip?: string | Nullish;
  actions?: LabelActions;
}

export function Label(props: LabelProps) {
  if (!isNotNilOrEmpty(props.children)) {
    return null;
  }

  const mainColor = props.isRecommended ? 'warning' : 'secondary';
  const hasWrappingActions = props.actions?.some((action) => action.overflowWrap === 'anywhere');

  return (
    <>
      <HStack spacing={4} justify="space-between" align="flex-start" wrap={hasWrappingActions}>
        <HStack spacing={1}>
          <label htmlFor={props.id} data-testid={props['data-testid']}>
            <Text size="xSmall" color={mainColor}>
              {`${props.isRequired ? REQUIRED_INDICATOR : ''}${props.children}`}
            </Text>
          </label>
          <Show when={props.tooltip}>
            <Tooltip description={props.tooltip}>
              <Icon value="action/help" color="text.secondary" size={4} tabIndex={0} />
            </Tooltip>
          </Show>
        </HStack>
        <Show when={props.isCounterVisible}>
          <Text size="xSmall" color="secondary" noWrap>
            {getMaxLengthCounterText(props.currentLength ?? 0, props.maxLength ?? 0)}
          </Text>
        </Show>
        <Show when={isNotNilOrEmpty(props.actions)}>
          <HStack
            spacing={2}
            minWidth={hasWrappingActions ? 0 : undefined}
            maxWidth={hasWrappingActions ? '100%' : undefined}
            wrap={hasWrappingActions}
          >
            {props.actions?.map((action) => (
              <Link
                key={action.title}
                title={action.title}
                onClick={action.onClick}
                leftIcon={action.leftIcon}
                rightIcon={action.rightIcon}
                isDisabled={action.isDisabled}
                size="xSmall"
                href={action.href}
                target={action.target}
                overflowWrap={action.overflowWrap}
              />
            ))}
          </HStack>
        </Show>
      </HStack>
      <Space vertical={1} />
    </>
  );
}

const REQUIRED_INDICATOR = '* ';
